import * as React from "react";
import { Button, FormGroup, InputGroup, Tag } from "@blueprintjs/core";
import { css } from "@emotion/core";

export type StringListEditorProps = {
  value: string[];
  onChange: (items: string[]) => void;
  label: string;
  inputId: string;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Applied before the duplicate check. Defaults to trim. */
  normalize?: (raw: string) => string;
  /** Blocks Add when false. Defaults to accepting everything. */
  validate?: (normalized: string) => boolean;
  /** When set, the in-progress draft is submitted under this name so a parent
   *  form can commit it without the user clicking Add. */
  inputName?: string;
};

export function StringListEditor(props: StringListEditorProps) {
  const {
    value,
    onChange,
    label,
    inputId,
    helperText,
    placeholder,
    disabled,
    normalize = (raw) => raw.trim(),
    validate = () => true,
    inputName,
  } = props;

  const [newItem, setNewItem] = React.useState("");

  const handleAddItem = () => {
    const normalized = normalize(newItem);
    if (normalized && validate(normalized) && !value.includes(normalized)) {
      onChange([...value, normalized]);
      setNewItem("");
    }
  };

  const handleRemoveItem = (itemToRemove: string) => {
    onChange(value.filter((item) => item !== itemToRemove));
  };

  const normalizedDraft = normalize(newItem);
  const canAdd =
    !!normalizedDraft &&
    validate(normalizedDraft) &&
    !value.includes(normalizedDraft) &&
    !disabled;

  return (
    <FormGroup label={label} labelFor={inputId} helperText={helperText}>
      <div
        css={css`
          display: flex;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        `}
      >
        <InputGroup
          id={inputId}
          name={inputName}
          placeholder={placeholder}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddItem();
            }
          }}
          disabled={disabled}
          css={css`
            flex: 1;
          `}
        />
        <Button
          text="Add"
          intent="primary"
          onClick={handleAddItem}
          disabled={!canAdd}
        />
      </div>
      {value.length > 0 && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            margin: 1rem 0;
          `}
        >
          {value.map((item) => (
            <Tag key={item} onRemove={() => handleRemoveItem(item)} large>
              {item}
            </Tag>
          ))}
        </div>
      )}
    </FormGroup>
  );
}
